import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { EnvironmentTypes } from '../../models/environments';
import { CURRENT_ENVIRONMENT_TOKEN } from '../../providers/environment-providers';
import { PlatformDataService } from '../../services/platform-data';
import { DskHomeUserStore } from '../../store/dskhome-user/dskhome-user.store';

/**
 * guardedEnvironments: array of environments for which the guard will be applied (if not provided, it will be applied everywhere)
 * allowFriendlyCustomer: if true, the guard will allow friendly customers (company email domain)
 */
export interface MaintenanceGuardOptions {
  guardedEnvironments?: EnvironmentTypes[];
  allowFriendlyCustomer?: boolean;
}

const FRIENDLY_CUSTOMER_DOMAIN = 'dskbank.bg';

/**
 * This guard prevents users from accessing a route and redirects them to the maintenance page.
 *
 * The guard evaluates the following checks in order (first match wins):
 * 1. Server-side rendering — always allow
 * 2. Current environment is not in the guarded list — allow
 * 3. Friendly customer (company email domain) — allow
 * 4. Otherwise — redirect to /maintenance
 */
export const dskhomeMaintenanceEnvironmentGuardFactory = (
  options?: MaintenanceGuardOptions
): CanActivateFn => {
  return () => {
    const router = inject(Router);
    const platformDataService = inject(PlatformDataService);
    const currentEnvironment = inject(CURRENT_ENVIRONMENT_TOKEN);

    if (platformDataService.isServer) {
      return true;
    }

    if (isCurrentEnvironmentNotGuarded(options?.guardedEnvironments, currentEnvironment)) {
      return true;
    }

    if (shouldAllowFriendlyCustomer(options?.allowFriendlyCustomer)) {
      return true;
    }

    return new RedirectCommand(router.parseUrl('/maintenance'));
  };
};

/**
 * In case a guardedEnvironments list is provided but the current one is not within the list,
 * allow going to the page requested
 * @param guardedEnvironments
 * @param currentEnvironment
 */
function isCurrentEnvironmentNotGuarded(
  guardedEnvironments: EnvironmentTypes[] | undefined,
  currentEnvironment: EnvironmentTypes
): boolean {
  if (!guardedEnvironments) {
    return false;
  }

  return !guardedEnvironments.includes(currentEnvironment);
}

function shouldAllowFriendlyCustomer(allowFriendlyCustomer: boolean | undefined): boolean {
  if (!allowFriendlyCustomer) {
    return false;
  }

  const dskHomeUserStore = inject(DskHomeUserStore);
  const email = dskHomeUserStore.email();

  return isFriendlyCustomerEmail(email, FRIENDLY_CUSTOMER_DOMAIN);
}

function isFriendlyCustomerEmail(email: string | null | undefined, domain: string): boolean {
  if (!email) {
    return false;
  }

  const normalizedEmail = email.trim().toLowerCase();
  const expectedSuffix = `@${domain.toLowerCase()}`;

  // Includes guard from fake domains that are similar to the origin user@fake@dskbank.bg.
  return normalizedEmail.endsWith(expectedSuffix) && normalizedEmail.indexOf('@') === normalizedEmail.lastIndexOf('@');
}
